import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft } from 'lucide-react';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface DrivingConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void | Promise<void>;
}

const CONCEPT_PRESETS: { value: string; label: string; statement: string }[] = [
  { value: '', label: 'Custom concept (enter below)', statement: '' },
  {
    value: 'see_more',
    label: 'See More — help drivers notice hazards earlier',
    statement: 'A driver support solution that expands what the driver can notice by surfacing relevant hazards, movements, or changes around the vehicle earlier and more clearly.',
  },
  {
    value: 'understand_more',
    label: 'Understand More — make sense of complex situations',
    statement: 'A driver support solution that helps drivers make sense of complex situations by identifying what matters most, what may happen next, and what deserves immediate attention.',
  },
  {
    value: 'respond_better',
    label: 'Respond Better — right action at the right moment',
    statement: 'A driver support solution that helps drivers take better action at the right moment by reducing delay, hesitation, and missed response opportunities.',
  },
  {
    value: 'feel_more_capable',
    label: 'Feel More Capable — reduce cognitive load',
    statement: 'A driver support solution that reduces cognitive load and uncertainty so drivers feel calmer, more supported, and more in control.',
  },
];

const WEATHER_OPTIONS = [
  { value: 'clear', label: 'Clear' },
  { value: 'rain', label: 'Rain' },
  { value: 'fog', label: 'Fog' },
  { value: 'night', label: 'Night' },
];

const TRAFFIC_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'heavy', label: 'Heavy' },
];

const DEFAULT_POPULATION = 'population_driving_variance';

const DEFAULT_MODEL = 'o3';

const DrivingConceptTestForm: React.FC<DrivingConceptTestFormProps> = ({
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [conceptPreset, setConceptPreset] = useState(initialFormData.conceptPreset || '');
  const [conceptStatement, setConceptStatement] = useState(initialFormData.conceptStatement || '');
  const [weather, setWeather] = useState(initialFormData.weather || 'clear');
  const [traffic, setTraffic] = useState(initialFormData.traffic || 'moderate');
  const sampleSize = 100;
  const population = DEFAULT_POPULATION;
  const llmModel = DEFAULT_MODEL;

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    onFormDataChange({
      simName,
      conceptPreset,
      conceptStatement,
      sampleSize,
      weather,
      traffic,
      population,
      llmModel: DEFAULT_MODEL,
    });
  }, [simName, conceptPreset, conceptStatement, weather, traffic]);

  const handleFormChange = () => {
    if (onEditStep) onEditStep();
  };

  const handlePresetChange = (value: string) => {
    setConceptPreset(value);
    const preset = CONCEPT_PRESETS.find(p => p.value === value);
    if (preset && preset.statement) {
      setConceptStatement(preset.statement);
    }
    handleFormChange();
  };

  const isFormValid = conceptStatement.trim() !== '';

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        task: 'driving-concept-test',
        name: simName || `Driving Concept Test - ${conceptStatement.slice(0, 40)}`,
        concept_statement: conceptStatement,
        sample_size: sampleSize,
        population,
        weather,
        traffic,
        llm_model: llmModel,
      };

      if (onSavePayload) {
        await onSavePayload(payload);
      }
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClasses =
    'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white';
  const inputClasses =
    'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
        </div>

        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}
      </div>

      {/* Simulation Name */}
      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name (Optional)
        </label>
        <input
          id="simName"
          type="text"
          className={inputClasses}
          value={simName}
          onChange={(e) => { setSimName(e.target.value); handleFormChange(); }}
          placeholder="Enter a name for your simulation"
        />
      </div>

      {/* Concept */}
      <div className="mb-6">
        <label htmlFor="conceptPreset" className="block text-sm font-medium text-gray-700 mb-2">
          Concept <span className="text-red-500">*</span>
        </label>
        <select
          id="conceptPreset"
          className={selectClasses}
          value={conceptPreset}
          onChange={(e) => handlePresetChange(e.target.value)}
        >
          {CONCEPT_PRESETS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <div className="mt-3">
          <label htmlFor="conceptStatement" className="block text-sm text-gray-600 mb-1">
            Concept statement {conceptPreset ? '(editable)' : ''}
          </label>
          <textarea
            id="conceptStatement"
            rows={3}
            className={inputClasses}
            value={conceptStatement}
            onChange={(e) => { setConceptStatement(e.target.value); handleFormChange(); }}
            placeholder="Describe your driving concept to test..."
          />
        </div>
      </div>

      {/* Weather & Traffic */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="weather" className="block text-sm font-medium text-gray-700 mb-2">
            Weather
          </label>
          <select
            id="weather"
            className={selectClasses}
            value={weather}
            onChange={(e) => { setWeather(e.target.value); handleFormChange(); }}
          >
            {WEATHER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="traffic" className="block text-sm font-medium text-gray-700 mb-2">
            Traffic
          </label>
          <select
            id="traffic"
            className={selectClasses}
            value={traffic}
            onChange={(e) => { setTraffic(e.target.value); handleFormChange(); }}
          >
            {TRAFFIC_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting ? 'Processing...' : onSavePayload ? 'Continue' : 'Run Driving Concept Test'}
        </Button>
      </div>
    </div>
  );
};

export default DrivingConceptTestForm;
